import React, { useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Table } from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  fetchSessionsList,
  fetchSuppliersList,
  fetchTripsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { fetchTaxList } from "../../../slices/TaxMaster/thunk";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchChargesBillById } from "../../../slices/InvoiceCharges/thunk";
import { createSelector } from "reselect";

const validationSchema = Yup.object({});

const EditExtraChargeBill = ({ billId, onSubmit, onCloseForm }) => {
  const [initialTripsApplied, setInitialTripsApplied] = useState(false);

  const dispatch = useDispatch();

  const [totals, setTotals] = useState({
    tollCharges: 0,
    handlingCharges: 0,
    otherCharges: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      trips: state.Trip.trips,
      loading: state.Trip.loading,
      taxes: state.TaxMaster.taxes,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,
      session: state.Session.sessions,
      billDetails: state.InvoiceCharges.selectedChargesBill,
      suppliers: state.Supplier.suppliers,
    })
  );

  const {
    trips,
    loading,
    taxes,
    customersList,
    session,
    billDetails,
    vehicles,
    suppliers,
  } = useSelector(selector);

  /* ---------------------------------------
     FETCH INITIAL DATA
  -----------------------------------------*/
  useEffect(() => {
    if (!billId) return;
    dispatch(fetchTaxList({ isPagination: 0 }));
    dispatch(fetchVehiclesList({ isPagination: 0 }));
    dispatch(fetchCustomersList({ isPagination: 0 }));
    dispatch(fetchSessionsList({ isPagination: 0 }));
    dispatch(fetchSuppliersList({ isPagination: 0 }));
    dispatch(fetchChargesBillById(billId));
  }, [dispatch, billId]);

  /* ---------------------------------------
     WHEN BILL DETAILS ARRIVE → LOAD TRIPS
  -----------------------------------------*/
  useEffect(() => {
    if (billDetails?.customerId) {
      dispatch(
        fetchTripsList({
          customerId: billDetails?.customerId,
          sessionId: billDetails?.sessionId,
          vehicleId: billDetails?.chargesInvoiceResponses?.[0]?.vehicleId,
          // isChargesBill: false,
          isPagination: 0,
        })
      );
    }
  }, [dispatch, billDetails?.customerId]);

  /* ---------------------------------------
     DATE PARSER
  -----------------------------------------*/
  const convertDate = (d) => {
    if (!d) return new Date();
    if (d instanceof Date && !isNaN(d)) return d;
    const parts = String(d).split("-");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const dt = new Date(`${year}-${month}-${day}`);
      return isNaN(dt) ? new Date() : dt;
    }
    const dt2 = new Date(d);
    return isNaN(dt2) ? new Date() : dt2;
  };

  /* ---------------------------------------
     TOTAL CALCULATION
  -----------------------------------------*/
  const calculateTotals = (selectedTripIds = []) => {
    const selectedTrips = trips.filter((t) =>
      selectedTripIds.includes(String(t.id))
    );

    const tollCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.tollCharges) || 0),
      0
    );
    const handlingCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.handlingCharges) || 0),
      0
    );
    const otherCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.otherCharges) || 0),
      0
    );

    const grandTotal = tollCharges + handlingCharges + otherCharges;

    setTotals({
      tollCharges,
      handlingCharges,
      otherCharges,
      grandTotal,
    });
  };

  const initialTripIdsFromBill = (bd) => {
    if (!bd) return [];
    const arr = bd.chargesInvoiceResponses || [];
    return arr.map((x) => String(x.dailyTripId));
  };

  const initialSessionId = (bd) => bd?.sessionId || "";

  return (
    <div className="p-3">
      <h4 className="mb-3">Edit Bill</h4>

      <Formik
        enableReinitialize
        initialValues={{
          tollCharge: billDetails?.tollCharges || 0,
          otherCharge: billDetails?.otherCharges || 0,
          handlingCharge: billDetails?.handlingCharges || 0,
          totalCharge:
            (billDetails?.tollCharges || 0) +
            (billDetails?.otherCharges || 0) +
            (billDetails?.handlingCharges || 0),
          customerId: billDetails?.customerId || "",
          customerAddress: billDetails?.customerAddress || "",
          customerGSTNo: billDetails?.customerGSTNo || "",
          billDate: convertDate(billDetails?.billDate),
          tripIds: initialTripIdsFromBill(billDetails),
          selectedTaxId: billDetails?.taxId || billDetails?.selectedTaxId || "",
          sessionId: initialSessionId(billDetails),
          vehicleId: billDetails?.chargesInvoiceResponses?.[0]?.vehicleId || "",
          note: billDetails?.note || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const existingMap = {};
          (billDetails?.chargesInvoiceResponses || []).forEach((item) => {
            existingMap[String(item.dailyTripId)] = item.id; // keep existing DB row id
          });

          const d = values.billDate;
          const formattedBillDate =
            ("0" + d.getDate()).slice(-2) +
            "-" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "-" +
            d.getFullYear();

          const id = billDetails?.id;

          const payload = {
            sessionId: values.sessionId,
            billDate: formattedBillDate,
            customerId: values.customerId,
            customerAddress: values.customerAddress || "",
            customerGSTNo: values.customerGSTNo || "",
            tollCharges: totals.tollCharges || 0,
            handlingCharges: totals.handlingCharges || 0,
            otherCharges: totals.otherCharges || 0,
            totalAmount: totals.grandTotal || 0,
            note: values.note || 0,

            chargesInvoiceRequests: (values.tripIds || []).map((tripId) => ({
              id: existingMap[String(tripId)] || null, // keep old row id, null for new
              dailyTripId: tripId,
            })),
          };

          if (onSubmit) onSubmit(id, payload);
          if (onCloseForm) onCloseForm();
        }}
      >
        {({ values, setFieldValue }) => {
          // useEffect(() => {
          //   if (trips.length > 0 && billDetails) {
          //     const ids = initialTripIdsFromBill(billDetails).map(String);
          //     setFieldValue("tripIds", ids);
          //     calculateTotals(ids);
          //   }
          // }, [trips, billDetails]);

          useEffect(() => {
            if (!initialTripsApplied && trips.length > 0 && billDetails) {
              const ids = initialTripIdsFromBill(billDetails).map(String);
              setFieldValue("tripIds", ids);
              calculateTotals(ids);

              setInitialTripsApplied(true);
            }
          }, [trips, billDetails, initialTripsApplied]);

          const handleTripToggle = (tripId, checked) => {
            const id = String(tripId);
            const current = Array.isArray(values.tripIds)
              ? [...values.tripIds]
              : [];

            const updated = checked
              ? [...new Set([...current, id])]
              : current.filter((t) => t !== id);

            setFieldValue("tripIds", updated);
            calculateTotals(updated);
          };

          return (
            <Form>
              <Row className="g-4">
                {/* SESSION */}
                <Col md={3}>
                  <label className="form-label">
                    Session <span className="text-danger">*</span>
                  </label>

                  {(() => {
                    const selectedSessionObj = session?.find(
                      (s) => String(s.id) === String(values.sessionId)
                    );

                    const displayName =
                      selectedSessionObj?.name ||
                      billDetails?.sessionName ||
                      "";

                    return (
                      <>
                        <input
                          type="text"
                          className="form-control"
                          value={displayName}
                          disabled
                        />
                        <input
                          type="hidden"
                          name="sessionId"
                          value={values.sessionId || ""}
                        />
                      </>
                    );
                  })()}
                </Col>

                {/* CUSTOMER */}
                <Col md={3}>
                  <Label>Customer *</Label>
                  <Select
                    options={customersList.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label:
                              customersList.find(
                                (c) => c.id === values.customerId
                              )?.name || "",
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("customerId", opt?.value || "");

                      dispatch(
                        fetchTripsList({
                          customerId: opt?.value,
                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([]);
                    }}
                  />
                </Col>

                {/* VEHICLE */}
                <Col md={3}>
                  <Label>Vehicle *</Label>
                  <Select
                    options={vehicles.map((v) => ({
                      value: v.id,
                      label: v.vehicleNo,
                    }))}
                    value={
                      values.vehicleId
                        ? {
                            value: values.vehicleId,
                            label:
                              vehicles.find((v) => v.id === values.vehicleId)
                                ?.vehicleNo || "",
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("vehicleId", opt?.value || "");

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: opt?.value,
                          sessionId: values.sessionId,
                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([]);
                    }}
                  />
                </Col>

                {/* SUPPLIER */}
                <Col md={3}>
                  <Label>Supplier</Label>
                  <Select
                    options={suppliers.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const supplierId = opt ? opt.value : null;

                      setFieldValue("supplierId", supplierId);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId,
                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([]);
                    }}
                    isClearable
                  />
                </Col>

                {/* BILL DATE */}
                <Col md={3}>
                  <Label>Bill Date *</Label>
                  <Flatpickr
                    className="form-control"
                    value={values.billDate}
                    onChange={(dates) => setFieldValue("billDate", dates[0])}
                    options={{ dateFormat: "d-m-Y" }}
                  />
                </Col>
                <Col md={3}>
                  <Label>From Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select From Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.fromDate}
                      onChange={(date) => {
                        const fromDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;
                        setFieldValue("fromDate", fromDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: fromDateValue,
                            toDate: values.toDate,

                            isChargesBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId
                        );
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>To Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select To Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.toDate}
                      onChange={(date) => {
                        const toDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;

                        setFieldValue("toDate", toDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: values.fromDate,
                            toDate: toDateValue,
                            pageNo: 0,
                            isBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId
                        );
                      }}
                    />

                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                {/* TRIP TABLE */}
                <Col md={12}>
                  {/* <Label>Select Trip(s) *</Label>
                   */}

                  <div className="d-flex justify-content-between align-items-center">
                    <Label>Select Trips *</Label>

                    <div style={{ width: "285px" }}>
                      <Label className="mb-0">Note:</Label>
                      <Input
                        type="text"
                        value={values.note}
                        onChange={(e) => setFieldValue("note", e.target.value)}
                        placeholder="Enter note"
                      />
                    </div>
                  </div>

                  <Table bordered className="mt-2">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="8" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}

                      {!loading && trips.length === 0 && (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            No Record Found
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        trips.map((trip) => {
                          const checked = (values.tripIds || [])
                            .map(String)
                            .includes(String(trip.id));

                          return (
                            <tr key={trip.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    handleTripToggle(trip.id, e.target.checked)
                                  }
                                />
                              </td>
                              <td>{trip.customerName}</td>
                              <td>{trip.suppliersName}</td>
                              <td>{trip.tripDate}</td>
                              <td>{trip.vehicleNo}</td>
                              <td>{trip.rate}</td>
                              <td>{trip.kmOrTrip}</td>
                              <td>{trip.amount}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </Col>

                {/* TOTAL SUMMARY */}
                <Col md={12}>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                        <th>Grand Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>₹ {totals.tollCharges.toFixed(2)}</td>
                        <td>₹ {totals.handlingCharges.toFixed(2)}</td>
                        <td>₹ {totals.otherCharges.toFixed(2)}</td>
                        <td>
                          <b>₹ {totals.grandTotal.toFixed(2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* SUBMIT */}
                <Col md={12} className="text-end">
                  <Button color="success" className="btn-sm" type="submit">
                    Update Bill
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EditExtraChargeBill;
